import { useMutation, useQuery } from '@apollo/client';
import { Empty, message } from 'antd';
import {
  CasesSendReportV1CaseDetailsContainerSendReportCaseV1Document,
  CasesSendReportV1CaseDetailsContainerSendReportCaseV1UpdateDocument,
  CasesSendReportV1CaseDetailsContainerSendReportCaseV1UpdateMutationVariables,
  CasesSendReportV1CaseDetailsContainerStaffUserCurrentDocument,
  CasesSendReportV1CaseDetailsContainerStaffUsersDocument,
  StaffUser
} from '../../../../../../generated';
import { useOid } from '../../../../../../hooks/useOid';
import { CaseDetails } from '../../../../../shared/case-details/case-details';
import { ComponentQueryLoader } from '../../../../../shared/component-query-loader';
import { getStaffUserCurrentQueryOptionsWithOidAndCacheDisabled } from '../../../../../shared/utils/queryOptions';

interface CaseDetailsContainerProps {
  caseId: string;
}

export const CaseDetailsContainer: React.FC<CaseDetailsContainerProps> = (props) => {
  const oid = useOid();
  const staffUserCurrentQueryOptions = getStaffUserCurrentQueryOptionsWithOidAndCacheDisabled(oid);

  // Query to get the staff data
  const { data: staffData, loading: staffLoading, error: staffError } = useQuery(CasesSendReportV1CaseDetailsContainerStaffUsersDocument);

  const {
    data: currentStaffData,
    loading: currentStaffLoading,
    error: currentStaffError
  } = useQuery(CasesSendReportV1CaseDetailsContainerStaffUserCurrentDocument, staffUserCurrentQueryOptions);

  const {
    data: caseData,
    loading: caseLoading,
    error: caseError
  } = useQuery(CasesSendReportV1CaseDetailsContainerSendReportCaseV1Document, {
    variables: {
      sendReportCaseV1Id: props.caseId
    }
  });

  const [tagsUpdate] = useMutation(CasesSendReportV1CaseDetailsContainerSendReportCaseV1UpdateDocument, {});

  // Mutation to update the applicant's applying to data
  const [caseWorkerUpdate] = useMutation(CasesSendReportV1CaseDetailsContainerSendReportCaseV1UpdateDocument);

  const staffUsers =
    staffData?.staffUsers
      ?.filter((item): item is StaffUser => item !== null && item !== undefined)
      .filter((staff) => staff.role?.permissions?.sendCasePermissions?.canBeAssignedCases) || [];

  // Function to handle the save action
  const handleSave = async (data: StaffUser | null): Promise<void> => {
    try {
      const sendReportVariables: CasesSendReportV1CaseDetailsContainerSendReportCaseV1UpdateMutationVariables = {
        input: {
          id: props.caseId,
          caseDetails: {
            applicationReview: {
              caseWorkerAssigned: data?.id || null
            }
          }
        }
      };
      const { data: mutationData } = await caseWorkerUpdate({
        variables: sendReportVariables
      });
      if (mutationData?.sendReportCaseV1Update.status.success === true) {
        message.success('Assignment saved successfully');
      } else {
        message.error(`Error saving Assignment: ${mutationData?.sendReportCaseV1Update?.status.errorMessage}`);
      }
    } catch (error) {
      message.error(`Error saving Assignment: ${JSON.stringify(error)}`);
    }
  };

  const handleTagsChange = async (newTags: string[]): Promise<boolean> => {
    try {
      const sendReportUpdateVariables: CasesSendReportV1CaseDetailsContainerSendReportCaseV1UpdateMutationVariables = {
        input: {
          id: props.caseId,
          tags: newTags
        }
      };
      const result = await tagsUpdate({
        variables: sendReportUpdateVariables
      });
      if (!result.data?.sendReportCaseV1Update?.status.success) {
        message.error(`Error removing tags : ${result.data?.sendReportCaseV1Update?.status.errorMessage}`);
        return false;
      } else {
        message.success('tags updated successfully');
        return true;
      }
    } catch (error) {
      message.error(`Error saving tags: ${JSON.stringify(error)}`);
    }
    return false;
  };

  return (
    <ComponentQueryLoader
      loading={caseLoading || staffLoading || currentStaffLoading}
      hasData={caseData?.sendReportCaseV1 && staffData?.staffUsers}
      error={caseError || staffError || currentStaffError}
      hasDataComponent={
        <CaseDetails
          data={{
            caseData: {
              id: caseData?.sendReportCaseV1?.id || '',
              state: caseData?.sendReportCaseV1?.state || '',
              requestedAt: caseData?.sendReportCaseV1?.revisionRequest?.requestedAt || '',
              revisionSubmittedAt: caseData?.sendReportCaseV1?.revisionRequest?.revisionSubmittedAt || '',
              tags: caseData?.sendReportCaseV1?.tags || []
            }
          }}
          staffInfo={staffUsers}
          currentStaff={currentStaffData?.staffUserCurrent as StaffUser | undefined}
          assignedWorkerInfo={caseData?.sendReportCaseV1?.caseDetails?.applicationReview?.caseWorkerAssigned as StaffUser}
          onSave={handleSave}
          onTagsChange={handleTagsChange}
          isCurrentUser={caseData?.sendReportCaseV1?.isCurrentUserAssigned ?? false}
        />
      }
      noDataComponent={<Empty description="No data available" />}
    />
  );
};
